import React from "react";
import { Link } from "react-router-dom";
import Section from "../layout/Section";
import Statement from "../ui/Statement";
import MicroLabel from "../ui/MicroLabel";
import Reveal from "../anim/Reveal";
import Counter from "../anim/Counter";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./Split.module.css";

/*
 * Split — "CLIENT WORK / OUR OWN" (reference s07, ON TRACK / OFF TRACK).
 *
 * Olive, viewport-tall. Two halves meet at a centre seam: each is a two-line
 * lockup (condensed sans word over a serif accent word), one line of lead and a
 * .link-arrow, with a ghosted image bleeding in from the OUTER viewport edge.
 * The lime scribble sits under the serif word next to the seam. Below both
 * halves runs one hairline row of the four figures from SITE_CONTENT.statsBand
 * — this row replaces the old ProofBand section.
 *
 * Honesty: every sentence, caption and number is read from
 * src/constants/siteContent.js. The only strings typed here are the two lockups
 * and the two link labels (UI labels, no claims). The right half renders only
 * while ownProduct.status === "live"; otherwise the client half spans the row.
 *
 * Contrast is guaranteed by GEOMETRY, not by luck (see Split.module.css): the
 * copy column and the image box never overlap, and the image is capped at an
 * opacity where bone text stays >= 4.5:1 even over a pure white pixel.
 *
 * Motion: <Reveal> on the images, the lead + link and the figures; <Counter>
 * on the numbers. Both are reduced-motion safe in their own code. The two h2s
 * are left alone — HeadingWipe (App.js) clip-wipes every "main h2" itself.
 */

const WORK = SITE_CONTENT.caseStudies || {};
const PRODUCT = SITE_CONTENT.ownProduct || {};
const PROMO = PRODUCT.promo || {};
const STATS = SITE_CONTENT.statsBand || {};

/* TODO(content): the two lockups. `sans` sets in the condensed caps, `serif` is
   the accent word on the second line. One short word each — they are set at
   h2 scale side by side. Case does not matter (the headline is uppercased). */
const LOCKUPS = {
  client: { sans: "Client", serif: "work" },
  own: { sans: "Our", serif: "own" }
};

/* TODO(content): link labels and where they go. Both must be real routes. */
const LINKS = {
  client: { label: "View our work", to: "/case-studies" },
  own: { label: "See the app", to: "/zeta" }
};

/* TODO(content): which client screenshot sits behind the left half — an index
   into SITE_CONTENT.caseStudies.items (2 = Topsel Trucking). Its `label` is the
   caption, so the picture is always named correctly. */
const CLIENT_SHOT_INDEX = 2;

/* TODO(content): the Zeta creative behind the right half. The file name follows
   ownProduct.promo.hero.file, so swapping the flagship creative there swaps it
   here too. It is a ghosted backdrop (alt=""), never a readable offer. */
const OWN_SHOT_DIR = "/images/zeta/";
const OWN_SHOT_FALLBACK = "zeta-everything.jpg";

/* TODO(content): object-position of each backdrop — which part of the picture
   stays in the visible outer strip. "x% y%". */
const CLIENT_SHOT_FOCUS = "50% 50%";
const OWN_SHOT_FOCUS = "70% 50%";

/* TODO(content): separator between the two facts of the Zeta caption. */
const CAPTION_SEPARATOR = " · ";

/* Defensive read: a missing or mistyped field renders nothing rather than an
   empty shell or an invented placeholder. */
function text(value) {
  return typeof value === "string" ? value.trim() : "";
}

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

/* Split a figure into the numeric part <Counter> can animate plus whatever
   sits around it ("%", " wks", "h"). SITE_CONTENT already supplies numbers and
   an explicit suffix, but the client may later type "4+" straight into the
   value — this keeps that editable without breaking the count-up.
   (Carried over unchanged from ProofBand, which this row replaces.) */
function toFigure(item) {
  const raw = `${item.value}`;
  const match = raw.match(/-?\d[\d,]*\.?\d*/);
  const numeric = match ? Number(match[0].replace(/,/g, "")) : 0;
  const start = match ? match.index : 0;
  const prefix = match ? raw.slice(0, start) : "";
  const suffix =
    item.suffix != null
      ? item.suffix
      : match
      ? raw.slice(start + match[0].length)
      : "";

  return { value: numeric, prefix, suffix, label: item.label };
}

const WORK_ITEMS = Array.isArray(WORK.items) ? WORK.items : [];
const CLIENT_SHOT = WORK_ITEMS[CLIENT_SHOT_INDEX] || WORK_ITEMS[0] || {};
const OWN_SHOT_FILE = text(PROMO.hero && PROMO.hero.file) || OWN_SHOT_FALLBACK;
const IS_LIVE = PRODUCT.status === "live";

const HALVES = [
  {
    key: "client",
    lockup: LOCKUPS.client,
    scribble: true,
    lead: text(WORK.subtitle),
    link: LINKS.client,
    image: text(CLIENT_SHOT.screenshot),
    focus: CLIENT_SHOT_FOCUS,
    caption: text(CLIENT_SHOT.label)
  }
].concat(
  IS_LIVE
    ? [
        {
          key: "own",
          lockup: LOCKUPS.own,
          scribble: false,
          lead: text(PRODUCT.tagline),
          link: LINKS.own,
          image: `${OWN_SHOT_DIR}${OWN_SHOT_FILE}`,
          focus: OWN_SHOT_FOCUS,
          caption: [text(PRODUCT.name), text(PRODUCT.launchLabel)]
            .filter(Boolean)
            .join(CAPTION_SEPARATOR)
        }
      ]
    : []
);

const FIGURES = (Array.isArray(STATS.items) ? STATS.items : []).map(toFigure);

export default function Split() {
  const solo = HALVES.length === 1;

  return (
    <Section tone="olive" size="screen" container="none" id="split">
      <div className={styles.split}>
        <div className={cx(styles.halves, solo && styles.solo)}>
          {HALVES.map((half, index) => {
            /* "start" = image off the left edge, copy hugging the seam from the
               left. A lone half uses the "end" layout: copy left, image right. */
            const isStart = !solo && index === 0;

            return (
              <div
                key={half.key}
                className={cx(styles.half, isStart ? styles.start : styles.end)}
              >
                {half.image ? (
                  <Reveal
                    className={styles.media}
                    direction={isStart ? "left" : "right"}
                    aria-hidden="true"
                  >
                    <img
                      className={styles.image}
                      src={process.env.PUBLIC_URL + half.image}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                      style={{ objectPosition: half.focus }}
                    />
                  </Reveal>
                ) : null}

                {half.image && half.caption ? (
                  <MicroLabel as="span" className={styles.caption}>
                    {half.caption}
                  </MicroLabel>
                ) : null}

                <div className={styles.copy}>
                  <Statement as="h2" size="h2" className={styles.lockup}>
                    {half.lockup.sans}{" "}
                    <br />
                    <em className={half.scribble ? "scribble" : undefined}>
                      {half.lockup.serif}
                    </em>
                  </Statement>

                  <Reveal className={styles.detail} delay={120}>
                    {half.lead ? <p className={styles.lead}>{half.lead}</p> : null}
                    <div className={styles.action}>
                      <Link className="link-arrow" to={half.link.to}>
                        {half.link.label}
                      </Link>
                    </div>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>

        {FIGURES.length ? (
          <div className={styles.proof}>
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- list-style: none strips list semantics in Safari / VoiceOver */}
            <ul
              role="list"
              className={styles.stats}
              aria-label={text(STATS.title) || undefined}
              style={{ "--split-stat-count": FIGURES.length }}
            >
              {FIGURES.map((figure, i) => (
                <Reveal
                  key={figure.label || i}
                  as="li"
                  className={styles.stat}
                  direction="up"
                  delay={Math.min(i, 3) * 60}
                >
                  <span className={styles.figure}>
                    <Counter
                      value={figure.value}
                      prefix={figure.prefix}
                      suffix={figure.suffix}
                    />
                  </span>
                  <MicroLabel as="span" className={styles.statLabel}>
                    {figure.label}
                  </MicroLabel>
                </Reveal>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Section>
  );
}
